import { UAParser } from 'ua-parser-js';
import { merge } from 'lodash';
import request from '@oskecare/fe-utilities/lib/request';
import { uuid } from '@oskecare/fe-utilities/scripts/uuid';
import MyOrangeInterface from '@oskecare/fe-utilities/lib/MyOrangeInterface';
import { setTimedFlagFromUrlParam } from '@oskecare/fe-utilities/lib/timedFlag.js';
import { delay } from '@oskecare/fe-utilities/scripts/helpers.js';
import { throttle } from 'lodash/function';
import { bound } from '@oskecare/fe-utilities/lib/decorators';
import { formatBytes } from '@oskecare/fe-utilities/scripts/format';

export const DEBUG = 1;
export const INFO = 2;
export const WARNING = 3;
export const ERROR = 4;
export const CRITICAL = 5;
export const FATAL = 6;
export const SEVERITIES = [DEBUG, INFO, WARNING, ERROR, CRITICAL, FATAL];
export const SEVERITY_NAMES = {
  [DEBUG]: 'DEBUG',
  [INFO]: 'INFO',
  [WARNING]: 'WARNING',
  [ERROR]: 'ERROR',
  [CRITICAL]: 'CRITICAL',
  [FATAL]: 'FATAL',
};
export const LOGGER_SESSION_KEY = 'ec2fe-session';
export const LOGGER_DASET_KEY = 'ec2fe-daset';
export const LOGGER_DEBUG_MODE_KEY = 'ec2fe-debug-mode-expiration-time';
export const LOGGER_LOGS_KEY = 'ec2fe-logs';

// How long in milliseconds to wait for PerformanceObserver before logging a request.
export const PERFORMANCE_OBSERVER_DELAY = 100;

/**
 * Log collection and propagation service.
 *
 * Connects to the message bus, aggregates and formats received log messages,
 * and propagates the data to the backend, where they can be collected and processed.
 *
 * Logger is also responsible for formatting the messages, and processing and logging
 * thrown javascript errors.
 *
 * @property {MessageBus} _messageBus
 * @property {CustomerDataManager} _customerDataManager
 *
 * @property {object[]} _logs
 * @property {PerformanceEntry[]} _performanceEntries
 * @property {string} _instanceId
 * @property {number} _eventNumber
 * @property {?string} _legalType
 * @property {string} _sessionId
 * @property {boolean} _isInDebugMode
 * @property {boolean} _isDaset
 * @property {number} _sentBytes
 *
 * @property {string} _apiUrl
 * @property {boolean} _consoleOutput
 * @property {number} _minLoggedSeverity
 * @property {boolean} _sendToBackend
 */
export default class Logger {
  constructor({ messageBus, config }) {
    this._messageBus = messageBus;
    this._customerDataManager = null;

    this._logs = Logger._retrieveLogs();
    this._performanceEntries = [];
    this._instanceId = uuid();
    this._eventNumber = 1;
    this._legalType = null;
    this._sessionId = Logger.getSessionId();
    this._isInDebugMode = false;
    this._isDaset = false;
    this._sentBytes = 0;

    this._apiUrl = config.logApiUrl;
    this._consoleOutput = config.logConsoleOutput;
    this._minLoggedSeverity = config.logMinLoggedSeverity;
    this._sendToBackend = config.logSendToBackend;

    // TODO: replace with decorator, when decorator use lodash
    this._sendLogs = throttle(this._sendLogs, 10000, { leading: false, trailing: true });
  }

  /**
   * Logger initialization.
   *
   * Attaches itself onto MessageBus instance before doing anything with it.
   * @see MessageBus.attachLogger
   */
  initialize() {
    this._messageBus.attachLogger(this);
    this.initializeDebugMode();
    if (this._isInDebugMode) {
      this.debug('Debug Mode is ON.', 'infra-logger');
    }
    window.addEventListener('request-log', async event => {
      await this.logRequestEvent(event);
    });
    this.initializePerformanceObserver();
  }

  /**
   * Enter debug mode if it was initialized by a query parameter or was previously activated with a time limit
   * and the expiration time didn't elapse yet.
   *
   * If debug query is present it's processed and then removed from the URL. If query specifies a time limit,
   * the setting is persisted. Time limit must be specified by an integer in minutes.
   * @see setTimedFlagFromUrlParam
   */
  initializeDebugMode() {
    this._isInDebugMode = setTimedFlagFromUrlParam('debug', LOGGER_DEBUG_MODE_KEY);
  }

  /**
   * Setup `PerformanceObserver` to observe request/response timings - represented as a `PerformanceEntry` objects,
   * and save them in local `_performanceEntries` array. Only entries of type `resource` and of initiator type `fetch`
   * are saved.
   *
   * Entries are saved temporarily - as the request logs come in via `logRequestEvent`, corresponding entries
   * are matched up, and removed from the list.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Performance_API
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Performance_API/Resource_timing
   * @see https://developer.mozilla.org/en-US/docs/Web/API/PerformanceObserver
   */
  initializePerformanceObserver() {
    const observer = new PerformanceObserver(list => {
      list.getEntries().forEach(entry => {
        if (entry.initiatorType === 'fetch') {
          this._performanceEntries.push(entry);
        }
      });
    });

    observer.observe({ type: 'resource', buffered: true });
  }

  /**
   * Set daset flag if initial URL contains daset fragment.
   *
   * NOTE: Needs to be called in addition to `initialize` method, after auth initialization, the fragment
   * doesn't appear in return URL from SSO, only after the return `state` is resolved by auth initialization.
   */
  initializeDasetFlag() {
    const { hash } = new URL(window.location);
    const isDaset = hash === '#daset';

    if (!sessionStorage) {
      this._isDaset = isDaset;
      return;
    }
    const sessionValue = sessionStorage.getItem(LOGGER_DASET_KEY);
    this._isDaset = isDaset || sessionValue === 'true';
    sessionStorage.setItem(LOGGER_DASET_KEY, this._isDaset);
  }

  /**
   * @returns {string}
   */
  static getSessionId() {
    if (!sessionStorage) {
      return uuid();
    }

    const sessionId = sessionStorage.getItem(LOGGER_SESSION_KEY) ?? uuid();
    sessionStorage.setItem(LOGGER_SESSION_KEY, sessionId);
    return sessionId;
  }

  /**
   * To avoid cyclic dependency between the CustomerDataManager and Logger, CustomerDataManager instance attaches
   * itself onto Logger instance during initialization. This way we can use the single instance export pattern and still have both
   * instances invoking each other without a cyclic dependency.
   *
   * @param {CustomerDataManager} customerDataManager
   * @see {CustomerDataManager.initialize}
   */
  async attachCustomerDataManager(customerDataManager) {
    this._customerDataManager = customerDataManager;

    // Fetching legalType must be done during CustomerDataManager initialization, as the module isn't available during
    // Logger initialization, and fetching during logging event could trigger a loop if the operation failed and
    // attempted to log
    await this.fetchLegalType();
  }

  /**
   * @returns {Promise<void>}
   */
  async fetchLegalType() {
    try {
      this._legalType = (await this._customerDataManager.getCustomerAccountInfo()).legalType;
    } catch {
      this._legalType = null;
    }
  }

  /**
   * Catch function that will log errors and rethrows
   *
   * This method is mainly intended to be used on `request` function's promise, to add logging handling
   * before any business logic promise handling takes place. This is because, in certain circumstances we want
   * to suppress the request errors, but it's still desirable for the errors to be logged, and it's easy to forget
   * to add the logging logic after the fact, this catch can be used to wrap the promise in a point of common access.
   *
   * @param {RequestError|Error} error
   * @return {Promise<void>}
   */
  @bound
  async catchError(error) {
    await this.logErrorObject(error);
    throw error;
  }

  /**
   * @param {RequestError|Error} error
   * @return {Promise<void>}
   */
  async logErrorObject(error) {
    if (!error || error.wasLogged) {
      return;
    }
    // eslint-disable-next-line no-param-reassign
    error.wasLogged = true;

    const logSource = error.logSource ?? error.requestLogData?.logSource ?? 'uncaught-error';

    const severity = (() => {
      if (error.isExpected) {
        return WARNING;
      }
      if (error.isNonessential) {
        return ERROR;
      }
      if (error.isFatal) {
        return FATAL;
      }
      return CRITICAL;
    })();

    const performanceEntry = await this.findPerformanceEntry(error.requestLogData?.request?.url);

    this._addLog({
      message: error.message,
      source: logSource,
      severity,
      error,
      requestLogData: error.requestLogData,
      performanceEntry,
    });
  }

  /**
   * @param {RequestLogEvent} requestLogEvent
   * @return {Promise<void>}
   */
  async logRequestEvent(requestLogEvent) {
    const { requestLogData, isImportant } = requestLogEvent.detail;

    const performanceEntry = await this.findPerformanceEntry(requestLogData?.request?.url);

    this._addLog({
      message: `${requestLogData.request.method} ${requestLogData.request.url}`,
      source: requestLogData.logSource,
      severity: isImportant ? INFO : DEBUG,
      requestLogData,
      performanceEntry,
    });
  }

  /**
   * After a short delay, look through the `_performanceEntries` collected by the `PerformanceObserver` and try to
   * match one to the requested URL. Remove the entry from the list when matched. This way, assuming that both log
   * events and observer entries come in ordered, matching the first entry found will result in correctly matched up
   * timings and log events.
   *
   * @param {?string} url
   * @return {Promise<?PerformanceEntry>}
   */
  async findPerformanceEntry(url = null) {
    if (!url) {
      return null;
    }

    await delay(PERFORMANCE_OBSERVER_DELAY);

    let selectedEntry = null;
    const index = this._performanceEntries.findIndex(entry => entry.name === url);
    if (index !== -1) {
      selectedEntry = this._performanceEntries[index];
      this._performanceEntries.splice(index, 1);
    }

    return selectedEntry;
  }

  /**
   * Log a fatal error
   *
   * @param {string} message
   * @param {string} source
   */
  fatal(message, source) {
    return this._addLog({
      message,
      source,
      severity: FATAL,
    });
  }

  /**
   * Log a critical error
   *
   * @param {string} message
   * @param {string} source
   */
  critical(message, source) {
    return this._addLog({
      message,
      source,
      severity: CRITICAL,
    });
  }

  /**
   * Log an error
   *
   * @param {string} message
   * @param {string} source
   */
  error(message, source) {
    return this._addLog({
      message,
      source,
      severity: ERROR,
    });
  }

  /**
   * Log a warning
   *
   * @param {string} message
   * @param {string} source
   */
  warning(message, source) {
    return this._addLog({
      message,
      source,
      severity: WARNING,
    });
  }

  /**
   * Log an info message
   *
   * @param {string} message
   * @param {string} source
   */
  info(message, source) {
    return this._addLog({
      message,
      source,
      severity: INFO,
    });
  }

  /**
   * Log a debug message
   *
   * @param {string} message
   * @param {string} source
   */
  debug(message, source) {
    return this._addLog({
      message,
      source,
      severity: DEBUG,
    });
  }

  /**
   * Log a statistical info log
   *
   * @param {string} statName
   * @param {string} statGroup
   * @param {string} statCategory
   * @param {string} source
   */
  stat(statName, statGroup, statCategory, source) {
    return this._addLog({
      message: `STAT: ${statName}`,
      source,
      statName,
      statGroup,
      statCategory,
      severity: INFO,
    });
  }

  /**
   * Check if the severity should be logged.
   *
   * @param {number} severity
   * @returns {boolean} true if the severity is supposed to be logged
   * @private
   */
  _isSeverityLogged(severity) {
    if (!Logger._isSeverityValid(severity)) {
      throw new Error(`Invalid severity '${severity}'.`);
    }
    // in debug mode, all severities are logged
    return this._isInDebugMode || severity >= this._minLoggedSeverity;
  }

  /**
   * Checks if the parameter is a valid severity.
   *
   * @param {number} severity
   * @returns {boolean} true if the severity is valid one
   * @private
   */
  static _isSeverityValid(severity) {
    return SEVERITIES.some(item => item === severity);
  }

  /**
   * Insert a log entry
   *
   * @param {string} message
   * @param {string} source
   * @param {number} severity
   * @param {?Error} error
   * @param {?RequestLogData} requestLogData
   * @param {?PerformanceEntry} performanceEntry
   * @param {?string} statName
   * @param {?string} statGroup
   * @param {?string} statCategory
   * @private
   */
  _addLog({
    message,
    source,
    severity,
    error = null,
    requestLogData = null,
    performanceEntry = null,
    statName = null,
    statGroup = null,
    statCategory = null,
  }) {
    try {
      if (requestLogData?.doNotLog) {
        return; // Used to prevent loop by logging the log-sending requests
      }

      if (!this._isSeverityLogged(severity)) {
        return;
      }

      const logData = this._getEcsObject({
        message,
        source,
        severity,
        error,
        requestLogData,
        performanceEntry,
        statName,
        statGroup,
        statCategory,
      });

      // In debug mode console output is always on
      if (this._consoleOutput || this._isInDebugMode) {
        /* eslint-disable no-console */
        const consoleLogFunction = {
          [FATAL]: console.error,
          [CRITICAL]: console.error,
          [ERROR]: console.error,
          [WARNING]: console.warn,
          [INFO]: console.info,
          [DEBUG]: console.debug,
        }[severity];
        /* eslint-enable no-console */

        consoleLogFunction(
          `[${SEVERITY_NAMES[severity]}] (${source}): ${error ? error.stack : message}`,
        );
      }

      this._logs.push(logData);
      this._eventNumber += 1;
      Logger._persistLogs(this._logs);

      this._sendLogs();
    } catch (loggingError) {
      // eslint-disable-next-line no-console
      console.error(`[ERROR]: (logger): ${loggingError}`); // prevent loop in case of an error in logging
    }
  }

  /**
   * Persist current `_logs` array to the local storage
   *
   * @private
   */
  static _persistLogs(logs) {
    if (!localStorage) {
      return;
    }

    const json = JSON.stringify(logs);
    localStorage.setItem(LOGGER_LOGS_KEY, json);
  }

  /**
   * Retrieve logs from previous sessions if there are any saved in the local storage
   *
   * @return {object[]}
   * @private
   */
  static _retrieveLogs() {
    if (!localStorage) {
      return [];
    }

    try {
      const json = localStorage.getItem(LOGGER_LOGS_KEY);
      const logs = JSON.parse(json);
      if (!Array.isArray(logs)) {
        return [];
      }
      return logs;
    } catch {
      return [];
    }
  }

  /**
   * Take all the logs presently in the `_logs` array and send them to backend. Logs are sent asynchronously, so there's
   * a possibility that more logs will come in during the operation. As such the `_logs` array is only persisted at the
   * end, and in the case of an error, the logs are pushed back to the array (which now may also include newer logs).
   *
   * This method needs to be throttled, with the execution on the trailing event.
   *
   * @private
   */
  _sendLogs() {
    // Avoid sending empty array, if process is manually triggered without available logs
    if (this._logs.length === 0) {
      return;
    }

    const eventLogs = this._logs;
    this._logs = [];

    if (this._sendToBackend) {
      const requestBody = JSON.stringify(eventLogs);

      request({
        method: 'POST',
        route: `${this._apiUrl}/message`,
        requestProcessFunction: data => data,
        body: requestBody,
        doNotLog: true,
      })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error(error);
          this._logs.push(...eventLogs); // Put the unsent logs back
        })
        .finally(() => {
          Logger._persistLogs(this._logs);

          if (this._consoleOutput || this._isInDebugMode) {
            const bytes = requestBody.length;
            this._sentBytes += bytes;
            // eslint-disable-next-line no-console
            console.log(
              `[LOGGER] ${eventLogs.length} log entries sent. Request size: ${formatBytes(bytes, 'en-US', 2)}; total: ${formatBytes(this._sentBytes, 'en-US', 2)}`,
              eventLogs,
            );
          }
        });
    } else {
      // If sending is disabled just persist the emptied array.
      Logger._persistLogs(this._logs);
    }
  }

  /**
   * Create an ECS compatible log message.
   * https://www.elastic.co/guide/en/ecs/1.0/ecs-field-reference.html
   *
   * @param {string} message
   * @param {string} source
   * @param {number} severity
   * @param {?(Error|RequestError|ModuleLoaderError)} error
   * @param {?RequestLogData} requestLogData
   * @param {?PerformanceEntry} performanceEntry
   * @param {?string} statName
   * @param {?string} statGroup
   * @param {?string} statCategory
   * @returns {Promise<object>}
   */
  _getEcsObject({
    message,
    source,
    severity,
    error = null,
    requestLogData = null,
    performanceEntry = null,
    statName = null,
    statGroup = null,
    statCategory = null,
  }) {
    const isMyOrange = MyOrangeInterface.isAnyMyOrangeInterfaceAvailable;

    // TODO: when header issue is solved, replace `fetchStart` metric with `requestStart` metric
    const duration = performanceEntry
      ? performanceEntry.responseEnd - performanceEntry.fetchStart
      : null;

    const ecs = {
      '@timestamp': new Date().toISOString(),
      ecs: {
        version: '1.4.0',
      },
      labels: {
        type: 'fe',
        source,
        severity: SEVERITY_NAMES[severity],
        client: isMyOrange ? 'myOrange' : 'browser',
        instance_id: this._instanceId,
        session_id: this._sessionId,
        traffic_type: this._isDaset ? 'daset' : 'natural',
      },
      message,
      user: {
        legal_type: this._legalType ?? '',
      },
      event: {
        id: `${this._instanceId}-${this._eventNumber}`,
        sequence: this._eventNumber,
        duration,
      },
    };
    merge(
      ecs,
      Logger._getErrorEcs(error),
      Logger._getEcsUrl(window.location.href),
      Logger._getEcsUserAgent(window.navigator.userAgent),
      Logger._getStatEcs({ statName, statGroup, statCategory }),
    );
    if (requestLogData) {
      merge(ecs, Logger._getRequestEcs(requestLogData));
    }
    return ecs;
  }

  /**
   * @param {?(Error|RequestError|ModuleLoaderError)} error
   * @returns {Promise<object>}
   * @private
   */
  static _getErrorEcs(error) {
    if (!error) {
      return {};
    }
    switch (error.name) {
      case 'RequestError':
        return Logger._getRequestErrorEcs(error);
      case 'ModuleLoaderError':
        return Logger._getModuleErrorEcs(error);
      default:
        return {
          error: {
            type: error.name === 'SyntaxError' ? 'syntax' : 'script',
            stack_trace: error.stack,
          },
        };
    }
  }

  /**
   * @param {RequestError} error
   * @returns {{error: {type: string}}}
   * @private
   */
  static _getRequestErrorEcs(error) {
    return {
      error: {
        type: error.requestLogData.response ? 'request' : 'network',
      },
    };
  }

  /**
   * @param {RequestLogData} requestLogData
   * @returns {{labels: {data_source: string}, tracing: {http: {request: {method: string, body: {content: *}}, response: {status_code: string, body: {content: *}}}, url: {original: string}}}}
   * @private
   */
  static _getRequestEcs(requestLogData) {
    return {
      labels: {
        data_source: requestLogData.dataSource,
      },
      tracing: {
        http: {
          request: {
            method: requestLogData.request.method,
            body: {
              content: requestLogData.requestBody,
            },
          },
          ...(requestLogData.response
            ? {
                response: {
                  status_code: requestLogData.response.status.toString(), // must be a string according to ECS
                  body: {
                    content: requestLogData.responseBody,
                  },
                },
              }
            : {}),
        },
        ...Logger._getEcsUrl(requestLogData.request.url),
      },
    };
  }

  /**
   * @param {ModuleLoaderError} error
   * @returns {{tracing: {url: {path: ?string, fragment: ?string, original: string, scheme: ?string, port: ?number, domain: ?string, query: ?string, full: ?string}}, error: {stack_trace: string, type: string}}}
   * @private
   */
  static _getModuleErrorEcs(error) {
    return {
      error: {
        type: 'module',
        stack_trace: error.stack,
      },
      tracing: {
        ...Logger._getEcsUrl(error.url),
      },
    };
  }

  /**
   * @param {string} userAgentString
   * @returns {{user_agent: {original: string, os: {name: string, version: string}, name, device?: {name: string}, version: string}}}
   * @private
   */
  static _getEcsUserAgent(userAgentString) {
    const parser = new UAParser(userAgentString);
    const result = parser.getResult();

    const ecs = {
      user_agent: {
        name: result.browser.name,
        original: result.ua,
        version: result.browser.version,
        os: {
          name: result.os.name,
          version: result.os.version,
        },
      },
    };

    if (result.device.vendor || result.device.model) {
      ecs.user_agent.device = {
        name: [result.device.vendor, result.device.model]
          .filter(deviceNamePart => deviceNamePart)
          .join(' '),
      };
    } else if (result.device.type) {
      ecs.user_agent.device = { name: result.device.type };
    }

    return ecs;
  }

  /**
   * Break down the URL to its constituent parts according to ECS schema.
   *
   * @param {string} originalUrl
   * @returns {{
   *   url: {
   *     path: ?string,
   *     fragment: ?string,
   *     original: string,
   *     scheme: ?string,
   *     port: ?number,
   *     domain: ?string,
   *     query: ?string,
   *     full: ?string
   *   }
   * }}
   * @private
   */
  static _getEcsUrl(originalUrl) {
    try {
      const url = new URL(originalUrl);

      return {
        url: {
          domain: url.hostname,
          fragment: url.hash,
          full: url.href,
          original: originalUrl,
          path: url.pathname,
          port: url.port ? Number(url.port) : null,
          query: url.search,
          scheme: url.protocol.replace(':', ''),
        },
      };
    } catch {
      // In case the string wasn't fully valid URL and object creation failed, log at least the original string.
      return {
        url: {
          original: originalUrl,
        },
      };
    }
  }

  /**
   * @param {?string} statName
   * @param {?string} statGroup
   * @param {?string} statCategory
   * @return {null}
   */
  static _getStatEcs({ statName, statGroup, statCategory }) {
    if (!statName && !statGroup && !statCategory) {
      return null;
    }
    return {
      labels: {
        stat_name: statName,
        stat_group: statGroup,
        stat_category: statCategory,
      },
    };
  }
}
